'use client';

import { useState, useEffect, useCallback } from 'react';

const DEFAULT_INTERVAL_MS = 1000;

type UseCountdownParams = {
  initialSeconds?: number;
  intervalMs?: number;
  autoStart?: boolean;
};

type UseCountdownReturn = {
  seconds: number;
  isActive: boolean;
  start: (seconds?: number) => void;
  reset: () => void;
};

/**
 * Generic countdown timer hook
 *
 * @param initialSeconds - Starting seconds for countdown (default: 0)
 * @param intervalMs - Interval in milliseconds (default: 1000)
 * @param autoStart - Start countdown immediately on mount (default: false)
 *
 * @example
 * const { seconds, isActive, start, reset } = useCountdown();
 * start(30); // Start 30 second countdown
 */
export const useCountdown = ({
  initialSeconds = 0,
  intervalMs = DEFAULT_INTERVAL_MS,
  autoStart = false,
}: UseCountdownParams = {}): UseCountdownReturn => {
  const [seconds, setSeconds] = useState(autoStart ? initialSeconds : 0);

  const isActive = seconds > 0;

  const start = useCallback(
    (newSeconds?: number) => {
      setSeconds(newSeconds ?? initialSeconds);
    },
    [initialSeconds],
  );

  const reset = useCallback(() => {
    setSeconds(0);
  }, []);

  useEffect(() => {
    if (seconds <= 0) {
      return;
    }

    const id = setInterval(() => {
      setSeconds((prev) => Math.max(0, prev - 1));
    }, intervalMs);

    return () => clearInterval(id);
  }, [seconds, intervalMs]);

  return { seconds, isActive, start, reset };
};
